import React, { useState, useContext, useEffect } from 'react';
import { SimplifiedChampion } from '../../../../../gameTypes';
import LevelControl from './HealthControl/LevelControl';
import HealthControl from './HealthControl/HealthControl';
import ResourceControl from './HealthControl/ResourceControl';
import SkillOrderControl from './HealthControl/SkillOrderControl';
import CritControl from './HealthControl/CritControl';
import RoleSelector from './HealthControl/RoleSelector';
import GameTimeControl from './HealthControl/GameTimeControl';
import ChampionSelector, { ChampionReplaceData } from './ChampionSelector';
import RuneConfig3 from './RuneConfig3';
import ItemShopPanel2 from './ItemShopPanel2';
import GameAssetsContext from '../../../../../context/GameAssetContext';
import SocketContext from '../../../../../../NewWebSocket/components/WebSocketAdapter';
import { ChampionConfig } from './settings';
import './CategoricalSettingsView.css';
import { runeIdMapping } from '../../../../../../NewWebSocket/utils/runeIdMapping';
import DataService from '../../../../../DataService';

type SettingsCategory = 'champion' | 'runes' | 'items' | 'skillOrder';

// ID-based rune selection type
export type RuneSelection = {
  primaryPath: string;
  primaryRunes: string[];
  secondaryPath: string;
  secondaryRunes: string[];
  shards: string[];
};

interface CategoricalSettingsViewProps {
  championConfig: ChampionConfig;
  champion: SimplifiedChampion;
  onChangeConfig: (updated: ChampionConfig) => void;
  onSave: (updated: ChampionConfig) => void;
  onClose: () => void;
  externalActiveCategory?: SettingsCategory | null;
  onCategoryChange?: (category: SettingsCategory | null) => void;
  onRuneUpdate?: (runeSelection: RuneSelection) => void;
  onItemUpdate?: (items: string[]) => void;
  onLevelUpdate?: (level: number) => void;
  onSkillOrderUpdate?: (skillOrder: string[]) => void;
  onRoleUpdate?: (role: string) => void;
  onQuestCompletedUpdate?: (completed: boolean) => void;
  onGameTimeUpdate?: (gameTime: number) => void;
}

const CategoricalSettingsView: React.FC<CategoricalSettingsViewProps> = ({
  championConfig,
  champion,
  onChangeConfig,
  onSave,
  onClose,
  externalActiveCategory,
  onCategoryChange,
  onRuneUpdate,
  onItemUpdate,
  onLevelUpdate,
  onSkillOrderUpdate,
  onRoleUpdate,
  onQuestCompletedUpdate,
  onGameTimeUpdate
}) => {
  const socket = useContext(SocketContext);
  const { gameWideAssets } = useContext(GameAssetsContext);
  
  // State for active category (only one can be open at a time)
  // Use external control if provided, otherwise use internal state
  const [internalActiveCategory, setInternalActiveCategory] = useState<SettingsCategory | null>(null);
  const activeCategory = externalActiveCategory !== undefined ? externalActiveCategory : internalActiveCategory;
  
  // API version for Data Dragon URLs
  const [apiVersion, setApiVersion] = useState<string>('15.9.1');
  
  // Fetch latest API version
  useEffect(() => {
    DataService.fetchLatestVersion().then(setApiVersion);
  }, []);
  
  // Local config state
  const [localConfig, setLocalConfig] = useState<ChampionConfig>(championConfig);
  
  // Item shop is always open
  
  // Local item inventory state for batching changes
  const [localInventory, setLocalInventory] = useState<string[]>([]);
  const [localBootsSlot, setLocalBootsSlot] = useState<string | null>(null); // 7th slot for Bot quest
  const [hasPendingItemChanges, setHasPendingItemChanges] = useState(false);
  
  // Check if Bot lane 7th slot is available
  const hasBotBootsSlot = (champion.role === 'bot') && (champion.roleQuestCompleted ?? true);

  // Update local config when external config changes
  useEffect(() => {
    setLocalConfig(championConfig);
  }, [championConfig]);

  // Initialize local inventory from champion data on component mount
  useEffect(() => {
    if (champion.inventory && champion.inventory.items) {
      const currentInventory = champion.inventory.items.map(item => item.id);
      setLocalInventory(currentInventory);
      setLocalBootsSlot(champion.inventory.bootsSlotItem?.id || null);
      setHasPendingItemChanges(false);
    }
  }, [champion.id]); // Only run when champion changes

  // Update local inventory from champion data (only when no pending changes)
  useEffect(() => {
    if (champion.inventory && champion.inventory.items && !hasPendingItemChanges) {
      const currentInventory = champion.inventory.items.map(item => item.id);
      setLocalInventory(currentInventory);
      setLocalBootsSlot(champion.inventory.bootsSlotItem?.id || null);
    }
  }, [champion.inventory, hasPendingItemChanges]);

  // Category definitions
  const categories = [
    { id: 'runes' as SettingsCategory, label: 'Runes' },
    { id: 'items' as SettingsCategory, label: 'Items' },
    { id: 'skillOrder' as SettingsCategory, label: 'Skill Order' },
    { id: 'champion' as SettingsCategory, label: 'Champion' }
  ];

  // Function to get default runes for a given path using IDs
  const getDefaultRunesForPath = (pathId: string): string[] => {
    switch(pathId) {
      case '8000': // Precision
        return ['8005', '9111', '9104', '8014']; // Press the Attack, Triumph, Legend: Alacrity, Coup de Grace
      case '8100': // Domination
        return ['8112', '8139', '8137', '8105']; // Electrocute, Taste of Blood, Sixth Sense, Relentless Hunter
      case '8200': // Sorcery
        return ['8229', '8226', '8210', '8236']; // Arcane Comet, Manaflow Band, Transcendence, Gathering Storm
      case '8400': // Resolve
        return ['8437', '8446', '8429', '8451']; // Grasp of the Undying, Demolish, Conditioning, Overgrowth
      case '8300': // Inspiration
        return ['8369', '8304', '8345', '8347']; // First Strike, Magical Footwear, Biscuit Delivery, Cosmic Insight
      default:
        return ['8005', '9111', '9104', '8014']; // Default to Precision
    }
  };

  // Convert the rune structure from SettingsGroup[] to the RuneSelection format using IDs
  const getRuneSelection = (): RuneSelection => {
    const primaryPath = "8000"; // Precision ID
    const secondaryPath = "8100"; // Domination ID
    
    const defaultRuneSelection: RuneSelection = {
      primaryPath: primaryPath,
      primaryRunes: getDefaultRunesForPath(primaryPath),
      secondaryPath: secondaryPath,
      secondaryRunes: ['8139', '8105'], // Taste of Blood, Relentless Hunter
      shards: ['5008', '5008', '5001'] // Adaptive Force, Adaptive Force, Health Scaling
    };
    
    if (localConfig.runePage) {
      return {
        primaryPath: localConfig.runePage.primaryPath || primaryPath,
        primaryRunes: localConfig.runePage.primaryRunes || getDefaultRunesForPath(primaryPath),
        secondaryPath: localConfig.runePage.secondaryPath || secondaryPath,
        secondaryRunes: localConfig.runePage.secondaryRunes || ['8139', '8105'],
        shards: localConfig.runePage.shards || ['5008', '5008', '5001']
      };
    }
    
    if (champion && champion.runePage) {
      if (champion.runePage.primaryPath) {
        defaultRuneSelection.primaryPath = champion.runePage.primaryPath;
      }
      
      if (champion.runePage.secondaryPath) {
        defaultRuneSelection.secondaryPath = champion.runePage.secondaryPath;
      }
      
      if (champion.runePage.primaryRunes && Array.isArray(champion.runePage.primaryRunes) && champion.runePage.primaryRunes.length > 0) {
        defaultRuneSelection.primaryRunes = [...champion.runePage.primaryRunes];
      } else if (champion.runePage.keystone) {
        const pathDefaultRunes = getDefaultRunesForPath(defaultRuneSelection.primaryPath);
        defaultRuneSelection.primaryRunes = [
          champion.runePage.keystone,
          pathDefaultRunes[1] || '',
          pathDefaultRunes[2] || '',
          pathDefaultRunes[3] || ''
        ].filter(Boolean);
      }
      
      if (champion.runePage.secondaryRunes && Array.isArray(champion.runePage.secondaryRunes) && champion.runePage.secondaryRunes.length > 0) {
        defaultRuneSelection.secondaryRunes = [...champion.runePage.secondaryRunes];
      }
      
      if (champion.runePage.shards && Array.isArray(champion.runePage.shards) && champion.runePage.shards.length > 0) {
        defaultRuneSelection.shards = [...champion.runePage.shards];
      }
      
      return defaultRuneSelection;
    }
    
    return defaultRuneSelection;
  };

  const [runeSelection, setRuneSelection] = useState<RuneSelection>(getRuneSelection());

  // Handle rune changes
  const handleRuneChange = (newRuneSelection: RuneSelection) => {
    setRuneSelection(newRuneSelection);
    
    const updatedConfig: ChampionConfig = {
      ...localConfig,
      runes: localConfig.runes.map(group => {
        return {
          ...group,
          settings: group.settings.map(setting => {
            if (setting.key === "primaryPath") {
              return { ...setting, value: newRuneSelection.primaryPath };
            }
            if (setting.key === "secondaryPath") {
              return { ...setting, value: newRuneSelection.secondaryPath };
            }
            if (setting.key === "primaryRunes") {
              return { ...setting, value: newRuneSelection.primaryRunes };
            }
            if (setting.key === "secondaryRunes") {
              return { ...setting, value: newRuneSelection.secondaryRunes };
            }
            if (setting.key === "shards") {
              return { ...setting, value: newRuneSelection.shards };
            }
            return setting;
          })
        };
      })
    };
    
    setLocalConfig(updatedConfig);
    onChangeConfig(updatedConfig);
    
    // Call the parent component's rune update handler (for static mode)
    if (onRuneUpdate) {
      onRuneUpdate(newRuneSelection);
    }
    
    // Call WebSocket handler (for WebSocket mode)
    if (socket) {
      socket.emit('set-runes', { championId: champion.id, runeSelection: newRuneSelection });
    }
  };

  // Handle champion replacement
  const handleReplaceChampion = (championData: ChampionReplaceData) => {
    if (socket) {
      socket.emit('replace-champion', {
        championId: champion.id,
        newChampion: {
          name: championData.name,
          level: championData.level || champion.level,
          items: championData.items || [],
          runes: championData.runes || {},
          skillOrder: championData.skillOrder || []
        }
      });
    }
  };

  // Handle item removal (local only, not sent to backend yet)
  const handleItemRemove = (itemId: string, indexToRemove?: number) => {
    // Check if it's the boots slot item
    if (localBootsSlot === itemId && indexToRemove === undefined) {
      setLocalBootsSlot(null);
      setHasPendingItemChanges(true);
      return;
    }
    
    // Remove only one instance, not all copies
    const newInventory = [...localInventory];
    const idx = indexToRemove !== undefined ? indexToRemove : newInventory.indexOf(itemId);
    if (idx >= 0) {
      newInventory.splice(idx, 1);
    }
    setLocalInventory(newInventory);
    setHasPendingItemChanges(true);
  };

  // Boot item IDs for checking if an item is boots
  const BOOT_ITEM_IDS = new Set([
    '1001', '3006', '3009', '3010', '3013', '3020', '3047', '3111', '3158',
    '3170', '3171', '3172', '3173', '3174', '3175', '3176'
  ]);
  
  const isBootsItem = (itemId: string) => BOOT_ITEM_IDS.has(itemId);

  // Helper: get itemGroups for an item from gameWideAssets
  const getItemGroups = (itemId: string): string[] => {
    const item = gameWideAssets?.itemsDetails?.find(i => i.id === itemId || parseInt(i.id) === parseInt(itemId));
    return (item as any)?.itemGroups || [];
  };

  // Check if adding an item would conflict with any existing inventory item's group
  const hasGroupConflict = (itemId: string): boolean => {
    const newGroups = getItemGroups(itemId);
    if (newGroups.length === 0) return false;
    const allItems = [...localInventory];
    if (localBootsSlot) allItems.push(localBootsSlot);
    for (const existingId of allItems) {
      const existingGroups = getItemGroups(existingId);
      for (const group of newGroups) {
        if (existingGroups.includes(group)) return true;
      }
    }
    return false;
  };

  // Helper: check if an item is legendary tier
  const isLegendaryItem = (itemId: string): boolean => {
    const item = gameWideAssets?.itemsDetails?.find(i => i.id === itemId || parseInt(i.id) === parseInt(itemId));
    return item?.tier === 'legendary';
  };

  // Handle item addition (local only, not sent to backend yet)
  const handleItemAdd = (itemId: string) => {
    // Only enforce "1 of each" for legendary items
    // Epic, basic, and starter items can be added multiple times
    if (isLegendaryItem(itemId) && (localInventory.includes(itemId) || localBootsSlot === itemId)) {
      return;
    }

    // Check item group conflict
    if (hasGroupConflict(itemId)) {
      console.log(`[CategoricalSettingsView] ⛔ Cannot add item ${itemId}: group conflict`);
      return;
    }

    const isBoots = isBootsItem(itemId);

    // If Bot quest is active and it's boots, try to add to boots slot
    if (hasBotBootsSlot && isBoots && !localBootsSlot) {
      setLocalBootsSlot(itemId);
      setHasPendingItemChanges(true);
      return;
    }

    // Regular inventory add (max 6 items)
    if (localInventory.length < 6) {
      const newInventory = [...localInventory, itemId];
      setLocalInventory(newInventory);
      setHasPendingItemChanges(true);
    }
  };

  // Save item changes to backend
  const saveItemChanges = () => {
    if (!hasPendingItemChanges) return;

    // Combine regular inventory with boots slot for full item list
    const allLocalItems = localBootsSlot 
      ? [...localInventory, localBootsSlot]
      : [...localInventory];

    // Use onItemUpdate if available (static mode), otherwise use socket (WebSocket mode)
    if (onItemUpdate) {
      onItemUpdate(allLocalItems);
    } else if (socket) {
      // Get current backend inventory (including boots slot)
      const currentBackendItems = champion.inventory?.items?.map(item => item.id) || [];
      const currentBackendBootsSlot = champion.inventory?.bootsSlotItem?.id || null;
      const allBackendItems = currentBackendBootsSlot 
        ? [...currentBackendItems, currentBackendBootsSlot]
        : [...currentBackendItems];

      // Diff that properly handles duplicate items
      const remainingBackend = [...allBackendItems];
      const remainingLocal = [...allLocalItems];

      // Match items present in both lists (consume one occurrence each)
      for (let i = remainingLocal.length - 1; i >= 0; i--) {
        const idx = remainingBackend.indexOf(remainingLocal[i]);
        if (idx >= 0) {
          remainingBackend.splice(idx, 1);
          remainingLocal.splice(i, 1);
        }
      }

      // remainingBackend = items to remove, remainingLocal = items to add
      remainingBackend.forEach(itemId => {
        socket.emit('remove-item', { championId: champion.id, itemId });
      });

      remainingLocal.forEach(itemId => {
        socket.emit('add-item', { championId: champion.id, itemId });
      });
    }

    // Clear pending changes flag immediately to prevent state conflicts
    setHasPendingItemChanges(false);
  };

  // Get item details
  const getItemDetails = (itemId: string) => {
    if (!gameWideAssets?.itemsDetails) return null;
    return gameWideAssets.itemsDetails.find(item => item.id === itemId);
  };

  // Calculate total inventory value using local inventory (including boots slot)
  const totalInventoryValue = React.useMemo(() => {
    if (!gameWideAssets?.itemsDetails) {
      return 0;
    }

    let total = localInventory.reduce((sum, itemId) => {
      const itemDetails = getItemDetails(itemId);
      return sum + (itemDetails?.cost || 0);
    }, 0);

    // Add boots slot value if present
    if (localBootsSlot) {
      const bootsDetails = getItemDetails(localBootsSlot);
      total += bootsDetails?.cost || 0;
    }

    return total;
  }, [localInventory, localBootsSlot, gameWideAssets?.itemsDetails]);

  // Toggle category (only one active at a time)
  const handleCategoryClick = (categoryId: SettingsCategory) => {
    const newCategory = activeCategory === categoryId ? null : categoryId;
    
    if (onCategoryChange) {
      // Use external control
      onCategoryChange(newCategory);
    } else {
      // Use internal state
      setInternalActiveCategory(newCategory);
    }
  };



  // Render category content
  const renderCategoryContent = (categoryId: SettingsCategory) => {
    if (activeCategory !== categoryId) return null;

    switch (categoryId) {
      case 'champion':
        return (
          <div className="category-content">
            {/* Only show ChampionSelector in live (WebSocket) mode */}
            {socket && (
              <ChampionSelector
                currentChampion={{
                  id: champion.id,
                  name: champion.name
                }}
                onSelectChampion={handleReplaceChampion}
                onCancel={() => {}}
              />
            )}
            <RoleSelector
              championId={champion.id}
              currentRole={champion.role || 'mid'}
              questCompleted={champion.roleQuestCompleted ?? true}
              onRoleUpdate={onRoleUpdate}
              onQuestCompletedUpdate={onQuestCompletedUpdate}
            />
            <LevelControl 
              championId={champion.id} 
              currentLevel={champion.level}
              maxLevel={champion.maxLevel || 18}
              onLevelUpdate={onLevelUpdate}
            />
            <HealthControl 
              championId={champion.id}
              currentHealth={Math.round(champion.stats.totalStats.currentHealth)}
              maxHealth={Math.round(champion.stats.totalStats.health)}
            />
            {champion.stats.primaryResource && champion.stats.primaryResource.type !== "None" && (
              <ResourceControl
                championId={champion.id}
                primaryResource={champion.stats.primaryResource}
              />
            )}
            <CritControl
              championId={champion.id}
              currentCritMode={champion.critMode || 'ExpectedValue'}
            />
            <GameTimeControl
              championId={champion.id}
              currentGameTime={champion.gameTime ?? 0}
              onGameTimeUpdate={onGameTimeUpdate}
            />
          </div>
        );

      case 'runes':
        return (
          <div className="category-content">
            <RuneConfig3
              runeSelection={runeSelection}
              onRuneChange={handleRuneChange}
              key={`runes-${champion.id}`}
            />
          </div>
        );

      case 'items':
        const itemImageBaseUrl = `https://ddragon.leagueoflegends.com/cdn/${apiVersion}/img/item/`;
        
        return (
          <div className="category-content">
            
            <div className="inventory-container">
              <div className={`current-items ${hasBotBootsSlot ? 'with-boots-slot' : ''}`}>
                {Array(hasBotBootsSlot ? 7 : 6).fill(null).map((_, index) => {
                  const itemId = localInventory[index];
                  
                  if (itemId) {
                    const itemDetails = getItemDetails(itemId);
                    return (
                      <div 
                        key={index} 
                        className="item-icon" 
                        onClick={() => {
                          if (window.confirm(`Do you want to remove ${itemDetails ? itemDetails.name : itemId}?`)) {
                            handleItemRemove(itemId, index);
                          }
                        }}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          handleItemRemove(itemId, index);
                        }}
                      >
                        {itemDetails ? (
                          <img 
                            src={`${itemImageBaseUrl}${itemDetails.image?.full || 'missing.png'}`} 
                            alt={itemDetails.name} 
                            title={itemDetails.name}
                          />
                        ) : (
                          <span>{itemId}</span>
                        )}
                      </div>
                    );
                  } else {
                    return (
                      <div 
                        key={index} 
                        className="item-icon empty-slot"
                      >
                        <span className="empty-slot-plus">+</span>
                      </div>
                    );
                  }
                })}
              </div>
              <button 
                className={`save-items-button ${hasPendingItemChanges ? 'has-changes' : 'no-changes'}`}
                onClick={saveItemChanges}
                disabled={!hasPendingItemChanges}
              >
                Save Items
              </button>
            </div>
            <div className="inventory-value">
              Total value: 
              <span className="stat-icon gold-icon"></span>
              <span className="gold-amount">{totalInventoryValue}</span>
              {hasBotBootsSlot && <span className="bot-quest-indicator"> (Bot Quest: 7 slots)</span>}
            </div>
            <ItemShopPanel2
              isOpen={true}
              currentInventory={localInventory}
              championId={champion.id}
              onItemAdd={handleItemAdd}
              championRole={champion.role || 'mid'}
              roleQuestCompleted={champion.roleQuestCompleted ?? true}
            />
          </div>
        );

      case 'skillOrder':
        return (
          <div className="category-content">
            <SkillOrderControl 
              championId={champion.id}
              championName={champion.name} 
              defaultSkillOrder={champion.defaultSkillOrder || []}
              currentSkillOrder={champion.currentSkillOrder || []}
              currentLevel={champion.level}
              onSkillOrderUpdate={onSkillOrderUpdate}
            />
          </div>
        );

      default:
        return null;
    }
  };

    // Function to render category icon
  const renderCategoryIcon = (categoryId: SettingsCategory) => {
    // This filter converts to #cdbe91 - the exact same color as the settings button text
    const goldFilter = 'brightness(0) saturate(100%) invert(82%) sepia(8%) saturate(1593%) hue-rotate(19deg) brightness(92%) contrast(86%)';
    
    switch (categoryId) {
      case 'runes':
        return (
          <img 
            src={gameWideAssets.runeIcon} 
            alt="Runes" 
            className="category-icon"
            style={{ 
              width: '16px', 
              height: '16px', 
              marginRight: '6px',
              filter: goldFilter
            }}
          />
        );
      case 'champion':
        return (
          <img 
            src={gameWideAssets.championIcon} 
            alt="Champion" 
            className="category-icon"
            style={{ 
              width: '16px', 
              height: '16px', 
              marginRight: '6px',
              filter: goldFilter
            }}
          />
        );
      case 'items':
        return (
          <img 
            src={gameWideAssets.itemIcon} 
            alt="Items" 
            className="category-icon"
            style={{ 
              width: '16px', 
              height: '16px', 
              marginRight: '6px',
              filter: goldFilter
            }}
          />
        );
      case 'skillOrder':
        return (
          <img 
            src={gameWideAssets.skillOrderIcon} 
            alt="Skill Order" 
            className="category-icon"
            style={{ 
              width: '16px', 
              height: '16px', 
              marginRight: '6px',
              filter: goldFilter
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="categorical-settings-view">
      <div className="category-buttons">
        {categories.map((category) => (
          <button
            key={category.id}
            className={`category-button ${activeCategory === category.id ? 'active' : ''}`}
            onClick={() => handleCategoryClick(category.id)}
          >
            {renderCategoryIcon(category.id)}
            <span className="category-label">{category.label}</span>
          </button>
        ))}
      </div>

      {activeCategory && (
        <div className="category-content-container">
          {categories.map((category) => (
            <div key={category.id} className="category-section">
              {renderCategoryContent(category.id)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CategoricalSettingsView; 